import type * as React from 'react';
import type { Control, FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import {
  invoiceFieldsFor,
  isInvoiceFieldEditable,
  isInvoiceFieldRequired,
} from '../field-model/invoice-field-model';
import { FieldShell, errorMessage } from './form-helpers';
import { CompanyField, TotalAmountField } from './model-form-widgets';
import { PROVISIONAL_STATUS_OPTIONS } from './provisional-invoice-schema';

import type {
  InvoiceFieldDef,
  InvoiceKindKey,
  InvoiceSide,
} from '../field-model/invoice-field-types';

// ── Model-driven invoice form fields ────────────────────────────────────────
//
// One renderer produces the editable scalar inputs for every invoice kind
// from the shared field model: which fields appear, their labels, input
// types, and per-kind/per-side editability all come from the registry, in
// document order. The per-kind dialogs stop carrying their own field lists.
//
// What the renderer deliberately does NOT draw:
//   - override-persisted fields (edited at Review; shown read-only by the
//     resolved-document section)
//   - relation fields (the issuing-bank / receiving-account selectors keep
//     their catalog-backed widgets in the dialog body)
//   - repeating 'lines' editors (the note lines section is its own widget)
//   - fields listed in DEFERRED_INPUTS for a kind: values carried by the
//     form defaults today without a visible input — surfacing them is a
//     separate decision from unifying the existing surfaces.

interface FieldExtras {
  readonly step?: string;
  readonly placeholder?: string;
  readonly note?: string;
  readonly options?: readonly string[];
  readonly locked?: boolean;
}

const FIELD_EXTRAS: Readonly<Record<string, FieldExtras>> = {
  currency: { options: ['USD', 'EUR'] },
  amount_qt_mt: { step: '0.001' },
  market_reference_price: { step: '0.01' },
  vat_amount: { step: '0.01', placeholder: '0.00 (leave blank if not on invoice)' },
  applied_downpayment_amount: { step: '0.01' },
  original_due_date: {
    note: "Note: Expected Due Date is auto-calculated from the ticket's Payment Trigger Event and Offset Days.",
  },
  status: {
    options: PROVISIONAL_STATUS_OPTIONS,
    locked: true,
    note: 'Status moves through invoice actions: Issue sets Issued (and assigns the number), recording payments sets Paid or Partially Paid, Cancel sets Cancelled.',
  },
};

// Fields whose values the form carries via defaults without a visible input
// today, per kind. Kept out of the rendered set so the model cutover does not
// silently add inputs the flows never had; surfacing each is its own call.
const DEFERRED_INPUTS: Readonly<Partial<Record<InvoiceKindKey, readonly string[]>>> = {
  Provisional: ['remarks', 'market_reference_date', 'applied_downpayment_amount'],
  Final_Settlement: [
    'remarks',
    'market_reference_date',
    'applied_downpayment_amount',
    'applied_provisional_amount',
  ],
};

export interface ModelFormFieldsProps<T extends FieldValues> {
  readonly kind: InvoiceKindKey;
  readonly side: InvoiceSide;
  readonly register: UseFormRegister<T>;
  readonly control: Control<T>;
  readonly errors: FieldErrors<T>;
  readonly disabled: boolean;
  readonly invoiceNumberPlaceholder?: string;
}

function isRenderedInput(def: InvoiceFieldDef, kind: InvoiceKindKey, side: InvoiceSide): boolean {
  if (def.storage === 'override' || def.storage === 'relation') return false;
  if (def.input === 'lines') return false;
  if (DEFERRED_INPUTS[kind]?.includes(def.key) === true) return false;
  // The status select renders locked (editability false) as a lifecycle hint;
  // every other non-editable field is display, not a form input.
  if (def.key !== 'status' && !isInvoiceFieldEditable(def, kind, side)) return false;
  return true;
}

export function modelFormFieldKeys(kind: InvoiceKindKey, side: InvoiceSide): readonly string[] {
  return invoiceFieldsFor(kind)
    .filter((def) => isRenderedInput(def, kind, side))
    .map((def) => def.formKey ?? def.key);
}

export function ModelFormFields<T extends FieldValues>(
  props: ModelFormFieldsProps<T>,
): React.JSX.Element {
  const fields = invoiceFieldsFor(props.kind).filter((def) =>
    isRenderedInput(def, props.kind, props.side),
  );
  return (
    <div className="flex flex-col gap-4">
      {fields.map((def) => (
        <ModelField key={def.key} def={def} props={props} />
      ))}
    </div>
  );
}

function ModelField<T extends FieldValues>({
  def,
  props,
}: {
  readonly def: InvoiceFieldDef;
  readonly props: ModelFormFieldsProps<T>;
}): React.JSX.Element {
  const name = (def.formKey ?? def.key) as Path<T>;
  const error = errorMessage(props.errors, name);
  const required = isInvoiceFieldRequired(def, props.kind, props.side);
  if (def.input === 'company') {
    return (
      <CompanyField
        name={name}
        label={def.label}
        control={props.control}
        disabled={props.disabled}
        required={required}
        error={error}
      />
    );
  }
  if (def.input === 'select') {
    return <SelectField def={def} name={name} error={error} required={required} props={props} />;
  }
  if (def.key === 'total_amount' && props.kind !== 'Advance') {
    return (
      <TotalAmountField
        name={name}
        label={def.label}
        register={props.register}
        control={props.control}
        error={error}
        required={required}
        disabled={props.disabled}
      />
    );
  }
  return <BasicField def={def} name={name} error={error} required={required} props={props} />;
}

function BasicField<T extends FieldValues>({
  def,
  name,
  error,
  required,
  props,
}: {
  readonly def: InvoiceFieldDef;
  readonly name: Path<T>;
  readonly error: string | undefined;
  readonly required: boolean;
  readonly props: ModelFormFieldsProps<T>;
}): React.JSX.Element {
  const extras = FIELD_EXTRAS[def.key] ?? {};
  const numeric = def.input === 'number';
  const inputType = def.input === 'textarea' ? 'text' : def.input;
  const placeholder =
    def.key === 'invoice_number' && props.invoiceNumberPlaceholder !== undefined
      ? props.invoiceNumberPlaceholder
      : extras.placeholder;
  return (
    <FieldShell id={name} label={def.label} error={error} required={required}>
      <Input
        id={name}
        type={inputType}
        inputMode={numeric ? 'decimal' : undefined}
        step={extras.step}
        min={numeric ? '0' : undefined}
        placeholder={placeholder}
        autoComplete={inputType === 'text' ? 'off' : undefined}
        disabled={props.disabled}
        {...props.register(name, numeric ? { valueAsNumber: true } : undefined)}
      />
      {extras.note !== undefined && <p className="text-muted-foreground text-xs">{extras.note}</p>}
    </FieldShell>
  );
}

function SelectField<T extends FieldValues>({
  def,
  name,
  error,
  required,
  props,
}: {
  readonly def: InvoiceFieldDef;
  readonly name: Path<T>;
  readonly error: string | undefined;
  readonly required: boolean;
  readonly props: ModelFormFieldsProps<T>;
}): React.JSX.Element {
  const extras = FIELD_EXTRAS[def.key] ?? {};
  const options = extras.options ?? [];
  return (
    <FieldShell id={name} label={def.label} error={error} required={required}>
      <Select
        id={name}
        disabled={props.disabled || extras.locked === true}
        {...props.register(name)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
      {extras.note !== undefined ? (
        <p className="text-muted-foreground text-xs">{extras.note}</p>
      ) : null}
    </FieldShell>
  );
}
